import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FolderKanban, Activity, Sparkles, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { relativeTime } from '@/lib/format';
import { DashboardCalendarWidget } from '@/components/calendar/DashboardCalendarWidget';
import type { ProjectDto } from '@cc/types';

export function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: qk.projects,
    queryFn: () => api.listProjects(),
  });

  const projects = data?.projects ?? [];
  const active = projects.filter((p) => p.status === 'ACTIVE');
  const totalActivities = projects.reduce((acc, p) => acc + p.activityCount, 0);
  const lastTouched = active
    .filter((p) => p.lastActivityAt)
    .sort((a, b) => (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''))[0];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Command Centre</h1>
        <p className="text-sm text-muted-foreground">
          Today's snapshot. Email, drafts, and the Brief 13 light up from Phase 2 onward.
        </p>
      </div>

      <DashboardCalendarWidget />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
        className="grid grid-cols-1 gap-3 md:grid-cols-3"
      >
        <StatCard
          icon={FolderKanban}
          label="Active projects"
          value={active.length.toString()}
          loading={isLoading}
        />
        <StatCard
          icon={Activity}
          label="Total activities"
          value={totalActivities.toString()}
          loading={isLoading}
        />
        <StatCard
          icon={Sparkles}
          label="Last touched"
          value={lastTouched?.name ?? 'nothing yet'}
          sub={lastTouched ? relativeTime(lastTouched.lastActivityAt) : undefined}
          loading={isLoading}
        />
      </motion.div>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium text-foreground">Active projects</h2>
          <Link
            to="/projects"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            All projects <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {isLoading ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {active.map((p) => (
              <PulseCard key={p.id} project={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  loading,
}: {
  icon: typeof FolderKanban;
  label: string;
  value: string;
  sub?: string;
  loading?: boolean;
}) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 6 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { type: 'spring', stiffness: 120, damping: 14 },
        },
      }}
      whileHover={{ y: -2 }}
    >
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-md bg-muted">
              <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
              {loading ? (
                <Skeleton className="mt-2 h-5 w-24" />
              ) : (
                <p className="mt-1 truncate text-lg font-medium">{value}</p>
              )}
              {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function PulseCard({ project }: { project: ProjectDto }) {
  return (
    <Link to={`/projects/${project.id}`} className="block">
      <Card className="transition-colors hover:border-foreground/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="truncate font-medium">{project.name}</p>
            <Badge variant="outline">{project.type.toLowerCase()}</Badge>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {project.activityCount} activities · last {relativeTime(project.lastActivityAt)}
          </p>
        </CardContent>
      </Card>
    </Link>
  );
}
